"use client"

import { format, parse } from "date-fns"
import type { Locale } from "date-fns"
import * as React from "react"

import { Input } from "@/components/ui/v2/input"
import { cn } from "@/lib/utils/cn"

interface DateInputProps {
  /**
   * used for Label semantics. 
   **/ 
  id?: string
  value?: Date
  onChange: (date: Date | undefined) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  /** date-fns locale for formatting and parsing (e.g. from getDateFnsLocale). */
  locale?: Locale
}

/**
 * A text input for dates that allows users to type dates directly.
 * Accepts formats like "Jan 15, 2024" or "01/15/2024"
 */
const DISPLAY_FORMAT = "MMM d, yyyy"

function formatDateDefault(date: Date): string {
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ]
  const month = months[date.getMonth()]
  const day = date.getDate()
  const year = date.getFullYear()
  return `${month} ${day}, ${year}`
}

function parseDateDefault(str: string): Date | undefined {
  if (!str.trim()) return undefined
  const monthNames: Record<string, number> = {
    jan: 0,
    feb: 1,
    mar: 2,
    apr: 3,
    may: 4,
    jun: 5,
    jul: 6,
    aug: 7,
    sep: 8,
    oct: 9,
    nov: 10,
    dec: 11,
  }
  const mmmDdYyyy = str.match(/^([a-z]{3})\s+(\d{1,2}),?\s+(\d{4})$/i)
  if (mmmDdYyyy) {
    const monthIndex = monthNames[mmmDdYyyy[1].toLowerCase()]
    if (monthIndex !== undefined) {
      const d = new Date(Number.parseInt(mmmDdYyyy[3], 10), monthIndex, Number.parseInt(mmmDdYyyy[2], 10))
      if (!Number.isNaN(d.getTime())) return d
    }
  }
  const slashFormat = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (slashFormat) {
    const [, first, second, year] = slashFormat
    const d = new Date(Number.parseInt(year, 10), Number.parseInt(first, 10) - 1, Number.parseInt(second, 10))
    if (!Number.isNaN(d.getTime())) return d
  }
  const isoFormat = str.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (isoFormat) {
    const [, year, month, day] = isoFormat
    const d = new Date(Number.parseInt(year, 10), Number.parseInt(month, 10) - 1, Number.parseInt(day, 10))
    if (!Number.isNaN(d.getTime())) return d
  }
  const parsed = new Date(str)
  if (!Number.isNaN(parsed.getTime())) return parsed
  return undefined
}

export function DateInput({
  id,
  value,
  onChange,
  placeholder = "MMM dd, yyyy",
  disabled = false,
  className,
  locale,
}: DateInputProps) {
  const [inputValue, setInputValue] = React.useState("")

  const formatDate = React.useCallback(
    (date: Date): string => {
      if (locale) {
        return format(date, DISPLAY_FORMAT, { locale })
      }
      return formatDateDefault(date)
    },
    [locale]
  )

  const parseDate = React.useCallback(
    (str: string): Date | undefined => {
      if (!str.trim()) return undefined
      if (locale) {
        try {
          const parsed = parse(str, DISPLAY_FORMAT, new Date(), { locale })
          if (!Number.isNaN(parsed.getTime())) return parsed
        } catch {
          // fall through to numeric formats
        }
      }
      return parseDateDefault(str)
    },
    [locale]
  )

  // Sync internal state with external value
  React.useEffect(() => {
    if (value) {
      setInputValue(formatDate(value))
    } else {
      setInputValue("")
    }
  }, [value, formatDate])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value)
  }

  const handleBlur = () => {
    const parsed = parseDate(inputValue)
    if (parsed) {
      onChange(parsed)
      setInputValue(formatDate(parsed))
    } else if (inputValue.trim() === "") {
      onChange(undefined)
    } else {
      // Reset to previous valid value
      setInputValue(value ? formatDate(value) : "")
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.currentTarget.blur()
    }
  }

  return (
    <Input
      id={id}
      type="text"
      value={inputValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      disabled={disabled}
      className={cn("text-sm border-border", className)}
    />
  )
}
